import React, { createRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash.get';
import {
  FlexBox,
  Typography,
  Box,
  SvgIconButton,
  Drawer,
  Button,
  InputField,
  SvgIcon,
  color
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { selectors as clientSelectors } from 'store/clients';
import { selectors as mediaSelectors, actions as mediaActions } from 'store/media';
import { validateFileName, isValidImage } from 'utils/validation';
import { ImageUploadPreview } from '../ImageUploadOverlay/ImageUploadPreview';
import { ImageEditDrawerProps } from '../../../types';
const INVALID_FILE_NAME = 'Invalid image name format';

export const ImageEditDrawer: React.FC<ImageEditDrawerProps> = (props: ImageEditDrawerProps) => {
  const { isOpen, onClose, image, onSuccess, halfPreview, imageType, configurationId } = props;

  const { successful, pending, response, error } = useSelector(mediaSelectors.selectAction);
  const clientId = useSelector(clientSelectors.selectClientId);
  const [createMedia, updateMedia, resetAction, setUploadImageError] = useActions([
    mediaActions.createMedia,
    mediaActions.updateMedia,
    mediaActions.resetAction,
    mediaActions.setUploadImageError
  ]);

  const [newFile, setNewFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [invalidFile, setInvalidFile] = useState<boolean>(false);
  const fileUploadRef = createRef<HTMLInputElement>();
  const previewUrl = `${process.env.REACT_APP_S3_ASSETS}/${clientId}/`;

  useEffect(() => {
    if (!isOpen) {
      setUploadImageError('');
      setNewFile(null);
      setFileUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (successful && response && isOpen) {
    onClose();
    onSuccess(response);
    resetAction();
  }

  const triggerFileSelector = () => {
    if (fileUploadRef.current) fileUploadRef.current.click();
  };

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = get(e, ['target', 'files', 0]);

    if (file) {
      const err = isValidImage(file);
      if (err) {
        setUploadImageError(err);
      } else {
        setUploadImageError('');
        setNewFile(file);
        setFileUrl(URL.createObjectURL(file));
        fileNameValidationFn(fileName);
      }
    }
  };

  const handleSave = () => {
    if (newFile && fileName) {
      // Uploading a brand new image
      createMedia({
        file: newFile,
        name: `${fileName}.${newFile.name.split('.').pop()}`,
        type: imageType,
        configurationId,
        mediaId: image.id
      });
    } else if (newFile) {
      // Uploading an image with the same name as the old one
      createMedia({
        file: newFile,
        name: `${image.name}.${newFile.name.split('.').pop()}`,
        type: imageType,
        configurationId,
        mediaId: image.id
      });
    } else {
      // Renaming an image but not modifying it
      updateMedia({
        type: imageType,
        configurationId,
        oldName: image.name,
        newName: fileName
      });
    }
  };

  const isSaveDisabled = !fileName && !fileUrl;
  // FILE NAME VALIDATION
  const fileNameValidationFn = async (value: string | number | undefined) => {
    if (value && value.toString().trim().length) {
      // VALIDATION SCHEMA
      const schema = validateFileName(INVALID_FILE_NAME);

      // VALIDATE FN
      const result = await schema.validate(value.toString().trim()).catch((err) => err);
      // IF THERE WAS ERROR MESSAGE - SET ERROR STATE AND FILE INVALID
      setUploadImageError(result && result.message);
      setInvalidFile(result && result.message);
      if ((!result || !result.message) && newFile) {
        const err = isValidImage(newFile);
        if (err) {
          setUploadImageError(err);
        }
      }
    }
  };

  const handleFilenameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFilename = event.target.value.trim();
    fileNameValidationFn(newFilename);
    setFileName(newFilename);
  };

  return (
    <Drawer open={isOpen} onClose={onClose} hasBorderRadius>
      {image && (
        <FlexBox direction="column" padding="6 0" width={500} bg="white">
          <FlexBox align="center" justify="between" padding="0 6">
            <Typography fontSize={32} fontWeight={600}>
              Edit Image
            </Typography>
            <SvgIconButton appearance="transparent" onClick={onClose} data-testid="update-image-x">
              <SvgIcon name="exit_circle" size={40} />
            </SvgIconButton>
          </FlexBox>

          <Box flexOverride="none" fullWidth height="1px" margin="6 0" sx={{ backgroundColor: '#EFEFEF' }} />
          <Box margin="0 6" width={250}>
            <Typography fontSize={22} sx={{ marginLeft: '20px' }}>
              Rename
            </Typography>
            <InputField
              placeholder={image.name}
              fullWidth
              size="medium"
              onChange={handleFilenameChange}
              helperText={fileNameValidationFn}
              inputProps={{ maxLength: 19 }}
            />
          </Box>
          <Box margin="0 6" width={430}>
            <Box fullWidth height="1px" margin="6 0" sx={{ backgroundColor: '#EFEFEF' }} />
            <Box margin="0 3">
              <Typography fontSize={22} sx={{ marginBottom: '10px' }}>
                Image Preview
              </Typography>
              <ImageUploadPreview
                width={280}
                fileUrl={fileUrl || `${previewUrl}${image.s3Name}`}
                halfPreview={halfPreview}
              />
              <Box margin="2 0" />
              <input
                type="file"
                id="file"
                ref={fileUploadRef}
                style={{ display: 'none' }}
                onChange={handleFileSelection}
                accept="image/png, image/jpeg"
                data-testid="file"
              />
              <Button appearance="white" customSize="small" width={280} onClick={triggerFileSelector}>
                Replace Image
              </Button>
            </Box>
            <Box fullWidth height="1px" margin="6 0" sx={{ backgroundColor: '#EFEFEF' }} />
            <Box margin="0 3">
              {error && (
                <Box margin="2 0">
                  <Typography color={color.RED}>{error}</Typography>
                </Box>
              )}
              <Button
                appearance="primary"
                customSize="medium"
                width={200}
                disabled={isSaveDisabled || invalidFile}
                onClick={handleSave}
                isLoading={pending}
              >
                Save
              </Button>
              <Box margin="6 0" />
            </Box>
          </Box>
        </FlexBox>
      )}
    </Drawer>
  );
};
